import { singleton } from "tsyringe";
import { Config } from "../config";
import { addJsonHeaders, fetchAndGetArrayBuffer, fetchAndGetText, fetchAndThrowHttpErrors, getCookieValue, setCookieValue, uuidv4 } from "../utils/helpers";
import { User } from "../models/user";
import { BehaviorSubject } from 'rxjs';
import * as _ from 'lodash';

const TOKEN_STORAGE_KEY = 'studio-token';
const USERNAME_STORAGE_KEY = 'studio-username';
const PASSWORD_STORAGE_KEY = 'studio-password';
const DEFAULT_TOKEN_EXPIRATION_DAYS = 14;

@singleton()
export class UserService {
  public bjToken$ = new BehaviorSubject<string>(null);
  public jwtToken$ = new BehaviorSubject<string>(null);
  public user$: BehaviorSubject<User> = new BehaviorSubject<User>(null);
  private tokenExchangeEndpointUrl = `${Config.BJ_TOKEN_EXCHANGE_URL}`;
  private userManagerEndpointUrl = `${Config.BJ_USER_MANAGER}`;

  get user() {
    return this.user$.value;
  }

  get profilePicture() {
    const staticAssets = "https://static.bluejeans.com/z10/webapp-assets/";
    if (this.user && this.user.profilePicture && this.user.profilePicture.length > 0) {
      return staticAssets + this.user.profilePicture;
    } else {
      return null;
    }
  }

  constructor() {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY) as string;
    const bjToken = getCookieValue(Config.BJ_AUTH_COOKIE_NAME);
    if (token) {
      this.jwtToken$.next(token);
    }

    if (bjToken) {
      this.bjToken$.next(bjToken);
    }
  }

  public async bluejeansLogin(username: string, password: string, rememberMe = false) {
    const token = await fetchAndThrowHttpErrors(`${this.userManagerEndpointUrl}oauth2/token`, {
      method: 'POST',
      headers: addJsonHeaders({}),
      body: JSON.stringify({
        username,
        password,
        grant_type: 'password'
      })
    }) as {
      access_token: string;
      expires_in: number;
      refresh_token: string;
      scope: {
        user: number;
      }
    };

    this.bjToken$.next(token.access_token);

    // expires_in is defined in seconds and setCookieValue expects days
    setCookieValue(Config.BJ_AUTH_COOKIE_NAME, token.access_token, token.expires_in / (3600 * 24));

    if (rememberMe) {
      localStorage.setItem(USERNAME_STORAGE_KEY, username);
      localStorage.setItem(PASSWORD_STORAGE_KEY, password);
    } else {
      localStorage.setItem(USERNAME_STORAGE_KEY, '');
      localStorage.setItem(PASSWORD_STORAGE_KEY, '');
    }

    await this.fetchInformationFromToken(token.scope.user);
  }

  public async bluejeansLoginFromToken(accessToken: string) {
    setCookieValue(Config.BJ_AUTH_COOKIE_NAME, accessToken, DEFAULT_TOKEN_EXPIRATION_DAYS);
    this.bjToken$.next(accessToken);

    const tokenInfo = await this.getTokenInformation();

    await this.fetchInformationFromToken(tokenInfo.user);
  }

  private async fetchInformationFromToken(userId: number) {
    await Promise.all([
      this.exchangeJWTToken(),
      this.getUserInformation(userId)
    ]);
  }

  private async getTokenInformation() {
    return await fetchAndThrowHttpErrors(`${this.userManagerEndpointUrl}oauth2/tokenInfo`, {
      method: 'GET',
      headers: this.addBlueJeansAuthenticationHeader(addJsonHeaders({}))
    }) as { user: number };
  }

  private async getUserInformation(userId: number) {
    const user = await fetchAndThrowHttpErrors(`${this.userManagerEndpointUrl}v1/user/${userId}`, {
      method: 'GET',
      headers: this.addBlueJeansAuthenticationHeader(addJsonHeaders({}))
    }) as User;

    this.user$.next(user);
  }

  private async exchangeJWTToken() {
    const jwt = await fetchAndThrowHttpErrors(`${this.tokenExchangeEndpointUrl}token-exchange/accesstoken`, {
      method: 'POST',
      headers: {},
      body: JSON.stringify({
        grantType: 'USER',
        token: this.bjToken$.value
      })
    });

    this.jwtToken$.next(jwt.token);
  }

  public addBlueJeansAuthenticationHeader(headers: any) {
    if (!this.bjToken$.value) {
      throw new Error('addBlueJeansAuthenticationHeader error : no valid token');
    }
    headers['Authorization'] = `Bearer ${this.bjToken$.value}`;
    return headers;
  }

  public addJWTAuthenticationHeader(headers: any) {
    if (!this.jwtToken$.value) {
      throw new Error('addJWTAuthenticationHeader error : no valid token');
    }
    headers['Authorization'] = `Bearer ${this.jwtToken$.value}`;
    return headers;
  }

  public getSavedCredentials() {
    return {
      username: localStorage.getItem(USERNAME_STORAGE_KEY) as string,
      password: localStorage.getItem(PASSWORD_STORAGE_KEY) as string
    };
  }

  public logout() {
    localStorage.setItem(TOKEN_STORAGE_KEY, '');
    setCookieValue(Config.BJ_AUTH_COOKIE_NAME, '', DEFAULT_TOKEN_EXPIRATION_DAYS);
    this.bjToken$.next(null);
    this.jwtToken$.next(null);
  }

  public retrieveTokenFromCookie() {
    if (!Config.BJ_AUTH_COOKIE_NAME) {
      return null;
    }
    return getCookieValue(Config.BJ_AUTH_COOKIE_NAME);
  }

}
